<template>
  <div>
    <b-container>
      <b-row class="mt-3 mb-1">
        <b-col cols="12" sm="6" class="mb-2 mb-sm-0 pr-sm-1">
          <intersect
            @enter="onVisiblePostButton"
            @leave="onInvisiblePostButton"
          >
            <b-button block variant="primary" :to="{ name: 'MessageAdd' }"
              >Post a message</b-button
            >
          </intersect>
        </b-col>
        <b-col cols="12" sm="6" class="pl-sm-1">
          <si-search-input />
        </b-col>
      </b-row>
      <b-row class="mb-1">
        <b-col cols="12">
          <si-auto-refresh @change="onChangeAutoRefresh" @refresh="onRefresh" />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <si-post
            v-for="post in posts"
            :key="post.id"
            :post="post"
            @destroy="onDelete($event)"
          />
        </b-col>
      </b-row>
    </b-container>
    <si-pagination
      :page="page"
      :pages-count="pagesCount"
      :route-name="routeName"
    />
  </div>
</template>

<script>
import AutoRefresh from "@/components/message-board/AutoRefresh";
import PaginationNav from "@/components/message-board/PaginationNav";
import Post from "@/components/message-board/Post";
import SearchInput from "@/components/message-board/SearchInput";
import errorHandler from "@/services/errorHandler";
import debounce from "debounce";
import Intersect from "vue-intersect";
import { confirmPostDelete } from "@/dialogs";

export default {
  name: "Home",
  components: {
    "si-auto-refresh": AutoRefresh,
    "si-pagination": PaginationNav,
    "si-post": Post,
    "si-search-input": SearchInput,
    Intersect,
  },
  data: () => ({
    fetchPosts: null,
    autoRefreshTimeout: null,
  }),
  computed: {
    autoRefreshInterval() {
      return this.$store.getters.autoRefreshInterval;
    },
    page() {
      let p = this.$route.query.p;
      if (p && +p > 0) {
        if (+p > +this.pagesCount) {
          return +this.pagesCount;
        }
        return +p;
      }
      return 1;
    },
    pagesCount() {
      return this.$store.getters.pagesCount;
    },
    posts() {
      return this.$store.getters.posts;
    },
    routeName() {
      return this.$route.name;
    },
  },
  methods: {
    autoRefresh() {
      this.autoRefreshTimeout = setTimeout(() => {
        // Only auto-refresh on page 1; otherwise,
        // reading position will appear to "jump"
        if (this.page === 1) {
          this.fetchPosts();
        }
        this.clearAutoRefreshTimeout();
        this.autoRefresh();
      }, this.autoRefreshInterval);
    },
    clearAutoRefreshTimeout() {
      clearTimeout(this.autoRefreshTimeout);
      this.autoRefreshTimeout = null;
    },
    deletePost(id) {
      this.$store.dispatch("showBusy");
      this.$store
        .dispatch("deletePost", { id })
        .then(() => {
          this.fetchPosts();
        })
        .catch((err) => {
          errorHandler(this, err);
          this.fetchPosts();
        });
    },
    onChangeAutoRefresh(value) {
      this.clearAutoRefreshTimeout();
      if (+value === 0) {
        return;
      }
      this.autoRefresh();
    },
    async onDelete(id) {
      let result = await confirmPostDelete(this);
      if (!result) return;
      this.deletePost(id);
    },
    onRefresh() {
      this.fetchPosts();
    },
    onInvisiblePostButton() {
      this.$store.dispatch("showPostButton");
    },
    onVisiblePostButton() {
      this.$store.dispatch("hidePostButton");
    },
  },
  watch: {
    page(value) {
      this.$store.dispatch("setPage", value);
      this.fetchPosts();
    },
  },
  created() {
    this.fetchPosts = debounce(() => {
      this.$store.dispatch("showBusy");
      return this.$store
        .dispatch("fetchPosts", {
          page: this.page,
        })
        .then(() => {
          this.$store.dispatch("hideBusy");
        })
        .catch((err) => {
          errorHandler(this, err);
          this.$store.dispatch("hideBusy");
        });
    }, 250);
  },
  mounted() {
    this.fetchPosts();
    if (this.autoRefreshInterval > 0) {
      this.autoRefresh();
    }
  },
  beforeDestroy() {
    this.clearAutoRefreshTimeout();
    this.fetchPosts.clear();
    this.$store.dispatch("hideBusy");
    this.$store.dispatch("hidePostButton");
  },
};
</script>
